import { useState } from 'react';
import { cn } from '@hrms/ui';
import { courseCategoryIcon, coverTintVar } from './cover.js';

export type CourseCoverSize = 'sm' | 'md' | 'lg';

export interface CourseCoverProps {
  title: string;
  category: string;
  /** Short-lived GET for an uploaded cover; `null`/absent → the generated category cover. */
  coverImageUrl?: string | null;
  /** `sm` is a list thumbnail (icon only), `md` a course card, `lg` a page header. */
  size?: CourseCoverSize;
  className?: string;
}

const ICON_SIZE: Record<CourseCoverSize, string> = {
  sm: 'size-8',
  md: 'size-12 sm:size-14',
  lg: 'size-16 sm:size-20',
};

const TITLE_SIZE: Record<CourseCoverSize, string> = {
  sm: '',
  md: 'text-base',
  lg: 'text-xl sm:text-2xl',
};

const PADDING: Record<CourseCoverSize, string> = {
  sm: 'p-2',
  md: 'p-4',
  lg: 'p-5 sm:p-7',
};

/**
 * A course's 16:9 cover (PLAN §10 "Covers everywhere").
 *
 * With an uploaded image it is that image, cropped to fill. Without one it is GENERATED: a gradient
 * in the category's tint token, a large category icon and the course title — so a library of
 * courses nobody designed covers for still reads as distinct, recognisable tiles rather than a grid
 * of grey boxes. An uploaded image whose short-lived link has expired (or fails to load) falls back
 * to the generated cover instead of a broken-image icon.
 *
 * Decorative (`aria-hidden`): every caller shows the course title as real text next to the cover,
 * so announcing it twice would only be noise for a screen-reader user.
 */
export function CourseCover({
  title,
  category,
  coverImageUrl,
  size = 'md',
  className,
}: CourseCoverProps) {
  // Remembers WHICH url failed, not just "failed": a new URL (a re-signed link, a freshly uploaded
  // cover) gets its own chance to load without a reset effect.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const showImage = Boolean(coverImageUrl) && failedUrl !== coverImageUrl;
  const tint = coverTintVar(category);
  const Icon = courseCategoryIcon(category);

  return (
    <div
      aria-hidden="true"
      data-testid="course-cover"
      data-cover={showImage ? 'image' : 'generated'}
      className={cn(
        'relative aspect-video w-full overflow-hidden rounded-lg bg-player-surface',
        className,
      )}
    >
      {showImage && coverImageUrl ? (
        <img
          src={coverImageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailedUrl(coverImageUrl)}
          className="absolute inset-0 size-full object-cover"
        />
      ) : (
        <div
          className={cn('absolute inset-0 flex flex-col justify-between text-cover-fg', PADDING[size])}
          style={{
            backgroundImage: `linear-gradient(135deg, ${tint}, color-mix(in oklab, ${tint} 62%, var(--player-surface)))`,
          }}
        >
          <Icon
            strokeWidth={1.5}
            className={cn(
              'pointer-events-none absolute opacity-90',
              size === 'sm'
                ? 'start-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rtl:translate-x-1/2'
                : 'end-[6%] top-1/2 -translate-y-1/2',
              ICON_SIZE[size],
            )}
          />
          {size !== 'sm' && (
            <>
              <span className="max-w-[65%] truncate text-[11px] font-semibold uppercase tracking-[0.12em] opacity-85">
                {category}
              </span>
              <span
                className={cn(
                  'line-clamp-2 max-w-[70%] font-display font-bold leading-tight',
                  TITLE_SIZE[size],
                )}
              >
                {title}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
